import BrowserDetector from "browser-dtector";

import { compareSemanticVersions } from "./SemverComparison";

type BrowserInfo = ReturnType<BrowserDetector["parseUserAgent"]>;
let browserInfo: BrowserInfo | undefined;

export function getBrowserInfo(): BrowserInfo {
  if (!browserInfo || isTesting()) {
    const detector = new BrowserDetector(window.navigator.userAgent);
    browserInfo = detector.parseUserAgent();
  }
  return browserInfo;
}

export enum Env {
  Local,
  Dev,
  Prod,
}

export function currentEnv(): Env {
  switch (window._ENV) {
    case "local":
      return Env.Local;
    case "dev":
      return Env.Dev;
    case "prod":
      return Env.Prod;
    default:
      throw new Error(`Undefined environment: ${window._ENV ?? "undefined"}`);
  }
}

export function isUnsupportedDesktopSafari(): boolean {
  const { name, shortVersion } = getBrowserInfo();
  // Beware, the name may be undefined when called early-on running on native_iOS (from DataDog)
  const normalizedName = name?.toLowerCase().trim();
  if (normalizedName === "safari" && shortVersion !== null) {
    return compareSemanticVersions(shortVersion, "15.2") === 1;
  }
  return false;
}

export function isUnsupportedMobileSafari(): boolean {
  const iOSVersion = getIOSVersion();
  if (iOSVersion.every((v) => v === 0)) {
    return false;
  }
  const [major, minor, _patch] = iOSVersion;
  return major < 15 || (major === 15 && minor < 2);
}

export function currentEnvIsDevOrLocal(): boolean {
  return currentEnv() === Env.Dev || currentEnv() === Env.Local;
}

export function isTesting(): boolean {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (globalThis as any).process?.env?.NODE_ENV === "test";
}

export function currentEnvIsLocalOrIsTesting(): boolean {
  return currentEnv() === Env.Local || isTesting();
}

export function mocksResponse(): boolean {
  return currentEnv() === Env.Local && !window?._NGROK_HOST;
}

// Rough check for touch device
export function isTouchDevice(): boolean {
  const userAgent = window.navigator.userAgent.toLowerCase();
  const ios = /iphone|ipod|ipad/.test(userAgent);
  return isAndroidEnv() || ios;
}

export function isIOSAppEnv(): boolean {
  const userAgent = window.navigator.userAgent.toLowerCase();
  const safari = /safari/.test(userAgent);
  const ios = /iphone|ipod|ipad/.test(userAgent);
  return ios && !safari;
}

export function getIOSVersion(): [number, number, number] {
  const userAgent = window.navigator.userAgent.toLowerCase();
  const ios = /iphone|ipod|ipad/.test(userAgent);
  if (ios) {
    const v = userAgent.match(/os (\d+)[_|.](\d+)[_|.]?(\d+)?/);
    if (v) {
      return [parseInt(v[1], 10), parseInt(v[2], 10), parseInt(v[3] || "0", 10)];
    }
  }

  return [0, 0, 0];
}

export function isChromeOSAppEnv() {
  return /\bCrOS\b/.test(window.navigator.userAgent);
}

function isIFrameEnv(): boolean {
  return window.parent.location !== window.parent.parent.location;
}

export function isNativeAndroidAppEnv(): boolean {
  const userAgent = window.navigator.userAgent.toLowerCase();
  return /com\.classdojo\.android/.test(userAgent);
}

export function isAndroidEnv(): boolean {
  const userAgent = window.navigator.userAgent.toLowerCase();
  return /android/.test(userAgent);
}

export function isEmbeddedEnv(): boolean {
  return isIOSAppEnv() || isIFrameEnv() || isNativeAndroidAppEnv();
}

export function isScreenshotEnv(): boolean {
  const urlSearchParams = new URLSearchParams(window.location.search);
  const screenshot = urlSearchParams.get("screenshot");
  return screenshot != null && ["true", "", "1"].includes(screenshot);
}

export function isWebGL2Supported(): boolean {
  const forcedWebGL1On = new URLSearchParams(window.location.search).get("webgl1") !== null;
  if (forcedWebGL1On) {
    return true;
  }
  if (globalThis.WebGL2RenderingContext === undefined) {
    return false;
  }

  const canvas = document.createElement("canvas");
  const gl = canvas.getContext("webgl2");
  return !!gl;
}

export class WebGL2Error extends Error {}
